'use client';

import { AppError, AppErrorCode } from '@repo/error';
import { t } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import type { SessionType } from '@repo/shared/schema';
import { UserType } from '@repo/shared/types';
import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useOrganizationStore } from '@/stores/organization-store';
import type { SessionUser } from '@/types/auth';
import { AuthClient } from './auth';

export const authClient = new AuthClient();

export type AppSession = {
  session: SessionType;
  user: SessionUser;
};

interface SessionProviderProps {
  children: React.ReactNode;
  initialSession: AppSession | null;
}

interface SessionContextValue {
  sessionData: AppSession | null;
  refreshSession: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new AppError(AppErrorCode.UNKNOWN_ERROR, {
      message: 'useSession must be used within a SessionProvider',
    });
  }

  if (!context.sessionData) {
    throw new AppError(AppErrorCode.UNAUTHORIZED, {
      get message() {
        return t('workspace.sessionNotFoundUserIsNotAuthenticated');
      },
    });
  }

  return {
    ...context.sessionData,
    refreshSession: context.refreshSession,
  };
};

export const useOptionalSession = () => {
  const context = useContext(SessionContext);

  if (!context) {
    throw new AppError(AppErrorCode.UNKNOWN_ERROR, {
      message: 'useOptionalSession must be used within a SessionProvider',
    });
  }

  return context;
};

export const SessionProvider = ({ children, initialSession }: SessionProviderProps) => {
  useLocale();

  const { fetchOrganizations, clearOrganizations } = useOrganizationStore();
  const [session, setSession] = useState<AppSession | null>(initialSession);

  // const location = useLocation();

  const refreshSession = useCallback(async () => {
    const newSession = await authClient.getSession();
    const isAdmin = newSession.user?.type === UserType.ADMIN;
    if (!newSession.isAuthenticated) {
      setSession(null);
      isAdmin && clearOrganizations();
      return;
    }
    if (!isAdmin) await fetchOrganizations();

    setSession({
      session: newSession.session,
      user: newSession.user,
    });
  }, []);

  useEffect(() => {
    const onFocus = () => {
      void refreshSession();
    };

    window.addEventListener('focus', onFocus);

    return () => {
      window.removeEventListener('focus', onFocus);
    };
  }, [refreshSession]);

  /**
   * Refresh session in background on navigation.
   */
  useEffect(
    () => {
      void refreshSession();
    },
    [
      // location.pathname
    ],
  );

  return (
    <SessionContext.Provider
      value={{
        sessionData: session,
        refreshSession,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
};
